/**
 * ItemValueFormatter - 统一Item值格式化器
 *
 * 扩展SearchConfigFormatter，提供统一的item值格式化机制
 * 解决JJForm组件和CommonList组件中的代码重复问题
 *
 * @author System Architect
 * @created 2025-09-16
 * @version 2.0.0
 *
 * 技术特性：
 * - 继承SearchConfigFormatter的所有功能
 * - 支持Vue组件中的响应式使用
 * - 提供单例模式的全局实例
 * - 支持多种配置格式和扩展格式化器
 * - ES2017兼容语法，Node.js 14.x环境
 */

import SearchConfigFormatter from './SearchConfigFormatter'
import { getDateprocessing } from '@/utils/index'

/**
 * 数值格式化器 - 处理数字格式化需求
 */
class NumberFormatter {
  constructor() {
    this.type = 'number'
  }

  format(item, value) {
    if (value === null || value === undefined || value === '') {
      return null
    }

    const numValue = parseFloat(value)
    if (isNaN(numValue)) {
      return null
    }

    try {
      // 支持配置格式化选项
      const options = item.formatter && item.formatter.options || {}
      const decimals = options.decimals !== undefined ? options.decimals : 2

      return numValue.toFixed(decimals)
    } catch (error) {
      console.warn('[NumberFormatter] 数值格式化失败:', item.key, value, error)
      return null
    }
  }
}

/**
 * 货币格式化器 - 处理货币显示格式
 */
class CurrencyFormatter {
  constructor() {
    this.type = 'currency'
  }

  format(item, value) {
    if (value === null || value === undefined || value === '') {
      return null
    }

    const numValue = parseFloat(value)
    if (isNaN(numValue)) {
      return null
    }

    try {
      const options = item.formatter && item.formatter.options || {}
      const currency = options.currency || '¥'
      const decimals = options.decimals !== undefined ? options.decimals : 2

      return currency + numValue.toFixed(decimals)
    } catch (error) {
      console.warn('[CurrencyFormatter] 货币格式化失败:', item.key, value, error)
      return null
    }
  }
}

/**
 * 布尔值格式化器 - 处理布尔值显示
 */
class BooleanFormatter {
  constructor() {
    this.type = 'boolean'
  }

  format(item, value) {
    if (value === null || value === undefined) {
      return null
    }

    try {
      const options = item.formatter && item.formatter.options || {}
      const trueText = options.trueText || '是'
      const falseText = options.falseText || '否'

      // ES2017兼容：显式类型转换
      const boolValue = value === true || value === 'true' || value === '1' || value === 1
      return boolValue ? trueText : falseText
    } catch (error) {
      console.warn('[BooleanFormatter] 布尔值格式化失败:', item.key, value, error)
      return null
    }
  }
}

/**
 * 增强的日期格式化器 - 扩展原有DateFormatter功能
 */
class EnhancedDateFormatter {
  constructor() {
    this.type = 'date'
  }

  /**
   * 格式化单个日期值 - 支持更多日期处理场景
   */
  format(item, value) {
    if (!value || typeof value !== 'string') {
      return null
    }

    try {
      // 处理getDateprocessing()直接调用
      if (value.includes('getDateprocessing(')) {
        return this.processGetDateprocessingCall(value)
      }

      // 处理模板字符串：${getDateprocessing(0)} 23:59:59
      if (value.includes('${getDateprocessing(')) {
        return this.processTemplateString(value)
      }

      // 处理其他日期函数调用（扩展功能）
      if (value.includes('getCurrentMonthFirst(') || value.includes('getToday(')) {
        return this.processOtherDateFunctions(value)
      }

      return null
    } catch (error) {
      console.warn('[EnhancedDateFormatter] 日期格式化失败:', item.key, value, error)
      return null
    }
  }

  /**
   * 处理getDateprocessing()函数调用
   */
  processGetDateprocessingCall(value) {
    const match = value.match(/getDateprocessing\(([^)]+)\)/)
    if (match) {
      const param = match[1]
      // ES2017兼容：处理字符串参数和数字参数
      const paramValue = param.startsWith("'") && param.endsWith("'")
        ? param.slice(1, -1)
        : parseInt(param)
      const dateResult = getDateprocessing(paramValue)
      return value.replace(match[0], dateResult)
    }
    return null
  }

  /**
   * 处理模板字符串
   */
  processTemplateString(value) {
    return value.replace(/\$\{getDateprocessing\(([^)]+)\)\}/g, function(match, param) {
      // ES2017兼容：使用function替代箭头函数
      const paramValue = param.startsWith("'") && param.endsWith("'")
        ? param.slice(1, -1)
        : parseInt(param)
      return getDateprocessing(paramValue)
    })
  }

  /**
   * 处理其他日期函数（预留扩展）
   */
  processOtherDateFunctions(value) {
    // 预留：支持getCurrentMonthFirst()、getToday()等函数
    return value
  }

  /**
   * 基于新配置格式的处理
   */
  formatByConfig(formatterConfig) {
    if (!formatterConfig || formatterConfig.type !== 'date_processor') {
      return null
    }

    try {
      const method = formatterConfig.method
      const params = formatterConfig.params || []

      if (method === 'getDateprocessing') {
        const param = params[0]
        return getDateprocessing(param)
      }

      return null
    } catch (error) {
      console.warn('[EnhancedDateFormatter] 配置格式化失败:', formatterConfig, error)
      return null
    }
  }
}

/**
 * 主ItemValueFormatter类 - 继承SearchConfigFormatter
 */
export default class ItemValueFormatter extends SearchConfigFormatter {
  constructor() {
    super()

    // 替换原有的DateFormatter为增强版本
    this.formatters.set('date', new EnhancedDateFormatter())

    // 注册新的格式化器
    this.formatters.set('number', new NumberFormatter())
    this.formatters.set('currency', new CurrencyFormatter())
    this.formatters.set('boolean', new BooleanFormatter())

    // 性能优化：结果缓存
    this.resultCache = new Map()
    this.maxResultCacheSize = 500
  }

  /**
   * 格式化单个item的value - Vue组件专用接口
   * @param {Object} item - 配置项
   * @param {*} value - 要格式化的值（可选，默认使用item.value）
   * @returns {*} 格式化后的值，失败时返回null
   */
  formatItemValue(item, value) {
    if (!item) {
      return null
    }

    // 使用传入的value，回退到item.value
    const targetValue = value !== undefined ? value : item.value

    // 构造临时item对象用于格式化
    const tempItem = Object.assign({}, item, { value: targetValue })

    // 使用缓存提升性能
    const cacheKey = this.generateCacheKey(tempItem)
    if (this.resultCache.has(cacheKey)) {
      return this.resultCache.get(cacheKey)
    }

    try {
      const result = this.formatItem(tempItem)

      // 缓存结果（限制缓存大小）
      if (this.resultCache.size >= this.maxResultCacheSize) {
        const firstKey = this.resultCache.keys().next().value
        this.resultCache.delete(firstKey)
      }
      this.resultCache.set(cacheKey, result)

      return result
    } catch (error) {
      console.warn('[ItemValueFormatter] formatItemValue失败:', item.key, targetValue, error)
      return null
    }
  }

  /**
   * 检查item是否需要格式化 - Vue组件优化使用
   * @param {Object} item - 配置项
   * @param {*} value - 要检查的值
   * @returns {boolean}
   */
  shouldFormat(item, value) {
    if (!item) {
      return false
    }

    const targetValue = value !== undefined ? value : item.value
    const tempItem = Object.assign({}, item, { value: targetValue })

    return this.needsFormatting(tempItem)
  }

  /**
   * 生成缓存键
   */
  generateCacheKey(item) {
    const keyParts = [
      item.key || '',
      item.type || '',
      JSON.stringify(item.value || ''),
      JSON.stringify(item.formatter || {})
    ]
    return keyParts.join('|')
  }

  /**
   * 批量格式化 - 扩展原有功能
   * @param {Array} items - 配置项数组
   * @param {Object} valueOverrides - 值覆盖对象 {key: value}
   * @returns {Object} 格式化结果 {key: formattedValue}
   */
  formatBatchWithOverrides(items, valueOverrides) {
    if (!Array.isArray(items)) {
      return {}
    }

    const result = {}
    const self = this

    items.forEach(function(item) {
      // ES2017兼容：使用function替代箭头函数
      if (item && item.key) {
        // 使用覆盖值或原始值
        const value = valueOverrides && valueOverrides[item.key] !== undefined
          ? valueOverrides[item.key]
          : item.value

        if (self.shouldFormat(item, value)) {
          const formatted = self.formatItemValue(item, value)
          if (formatted !== null) {
            result[item.key] = formatted
          }
        }
      }
    })

    return result
  }

  /**
   * 清理缓存
   */
  clearResultCache() {
    this.resultCache.clear()
  }

  /**
   * 获取缓存统计信息（调试用）
   */
  getCacheStats() {
    return {
      size: this.resultCache.size,
      maxSize: this.maxResultCacheSize,
      hitRatio: this.cacheHits / (this.cacheHits + this.cacheMisses) || 0
    }
  }
}

/**
 * 单例实例 - 全局共享
 */
let globalFormatterInstance = null

/**
 * 获取全局ItemValueFormatter实例
 * @returns {ItemValueFormatter}
 */
export function getGlobalFormatter() {
  if (!globalFormatterInstance) {
    globalFormatterInstance = new ItemValueFormatter()
  }
  return globalFormatterInstance
}

/**
 * 重置全局实例（测试用）
 */
export function resetGlobalFormatter() {
  globalFormatterInstance = null
}